import React, { useEffect } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiCheckCircleFill, PiPlus, PiTrash, PiStarFill } from "react-icons/pi";

import { SaasPricing } from "../../saas";
import { CorporatePricing } from "../../corporate";

export default function PricingSection(props: SectionContentProps) {
    const { content, onContentChange, isSelected } = props;
    const variant = content.variant || "v1"; // v1 = model-a, v2 = model-b
    
    if (variant === "saas") {
        return <SaasPricing {...props} />;
    }
    if (variant === "corporate") {
        return <CorporatePricing {...props} />;
    }

    const primaryColor = content.colorPalette?.primary || "#111827";
    const secondaryColor = content.colorPalette?.secondary || "#374151";
    const fontFamily = content.fontFamily || "inherit";

    // Initial Data Setup
    useEffect(() => {
        if (!content.plans || content.plans.length === 0) {
            onContentChange?.({
                ...content,
                title: "Scale with Precision",
                badgeText: "PLANS & PRICING",
                plans: [
                    {
                        name: "Creator",
                        price: "0",
                        period: "/mo",
                        desc: "Perfect for exploring the platform.",
                        features: ["3 Active Projects", "Community Components", "Basic Analytics"],
                        buttonText: "Start For Free",
                        popular: false
                    },
                    {
                        name: "Engineer",
                        price: "79",
                        period: "/mo",
                        desc: "For professionals who demand the best.",
                        features: ["Unlimited Projects", "Premium Components", "Custom Fonts", "Priority Support"],
                        buttonText: "Get Started",
                        popular: true
                    },
                    {
                        name: "Enterprise",
                        price: "249",
                        period: "/mo",
                        desc: "Total control for large organizations.",
                        features: ["Custom Design System", "Team Collaboration", "White-labeling", "Dedicated Manager"],
                        buttonText: "Contact Sales",
                        popular: false
                    }
                ]
            });
        }
    }, []);

    const plans = content.plans || [];

    const addPlan = () => {
        const newPlans = [...plans, {
            name: "New Plan",
            price: "99",
            period: "/mo",
            desc: "Plan description goes here.",
            features: ["Feature 1", "Feature 2"],
            buttonText: "Select Plan",
            popular: false
        }];
        onContentChange?.({ ...content, plans: newPlans });
    };

    const removePlan = (index: number) => {
        const newPlans = plans.filter((_: any, i: number) => i !== index);
        onContentChange?.({ ...content, plans: newPlans });
    };

    const updatePlan = (index: number, key: string, value: any) => {
        const newPlans = [...plans];
        newPlans[index] = { ...newPlans[index], [key]: value };
        onContentChange?.({ ...content, plans: newPlans });
    };

    const addFeature = (planIndex: number) => {
        const newPlans = [...plans];
        newPlans[planIndex].features = [...newPlans[planIndex].features, "New Feature"];
        onContentChange?.({ ...content, plans: newPlans });
    };

    const removeFeature = (planIndex: number, featureIndex: number) => {
        const newPlans = [...plans];
        newPlans[planIndex].features = newPlans[planIndex].features.filter((_: any, i: number) => i !== featureIndex);
        onContentChange?.({ ...content, plans: newPlans });
    };

    const updateFeature = (planIndex: number, featureIndex: number, value: string) => {
        const newPlans = [...plans];
        const newFeatures = [...newPlans[planIndex].features];
        newFeatures[featureIndex] = value;
        newPlans[planIndex] = { ...newPlans[planIndex], features: newFeatures };
        onContentChange?.({ ...content, plans: newPlans });
    };

    const togglePopular = (index: number) => {
        // Toggle popular status, optional: ensure only one is popular? enforcing single popular for now might be better UX
        const newPlans = plans.map((p: any, i: number) => ({
            ...p,
            popular: i === index ? !p.popular : false // Toggle clicked, disable others
        }));
        onContentChange?.({ ...content, plans: newPlans });
    };

    if (variant === "v2") {
        // "Model B" - Single All-Access Style (Lifetime Deal / Special Offer)
        // We will use specialized fields for this or map to the first plan if desired.
        // For consistent editing, let's use standard content fields for the main offer details 
        // and a specific plan-like structure for features if needed, or just use the first plan.

        // Let's use plans[0] as the data source for the "main offer" to keep it unified
        const offer = plans[0] || { features: [], price: "99", name: "Ultimate", buttonText: "Buy Now" };
        const offerIndex = 0;

        return (
            <section className={`px-6 py-24 bg-gray-50 ${isSelected ? "ring-2 ring-blue-500" : ""}`} style={{ fontFamily }}>
                <div className="max-w-6xl mx-auto">
                    <div className="bg-white rounded-[3rem] shadow-xl border border-gray-100 overflow-hidden relative">
                        {/* Hidden Add/Remove Plan buttons for V2 as it is single view, but if array is empty we might need to add one */}
                        {plans.length === 0 && isSelected && (
                            <div className="absolute top-4 left-4 z-50">
                                <button onClick={addPlan} className="bg-blue-500 text-white px-4 py-2 rounded">Initialize Offer</button>
                            </div>
                        )}

                        <div className="p-12 md:p-20 flex flex-col md:flex-row gap-20 items-center">
                            <div className="flex-1">
                                <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-8 leading-tight">
                                    <EditableText
                                        value={content.title || "One Simple Price"}
                                        onChange={(v) => onContentChange?.({ ...content, title: v })}
                                        placeholder="Headline"
                                    />
                                </h2>
                                <p className="text-xl text-gray-500 font-medium mb-10 leading-relaxed">
                                    <EditableText
                                        value={content.subDesc || "Get unlimited access to all features with a simple one-time payment."}
                                        onChange={(v) => onContentChange?.({ ...content, subDesc: v })}
                                        placeholder="Subheadline..."
                                        as="span"
                                    />
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                                    {(offer.features || []).map((f: string, i: number) => (
                                        <div key={i} className="flex items-center gap-3 font-bold text-gray-800 group relative">
                                            <PiCheckCircleFill className="text-xl flex-shrink-0" style={{ color: primaryColor }} />
                                            <EditableText value={f} onChange={(v) => updateFeature(offerIndex, i, v)} />
                                            {isSelected && (
                                                <button
                                                    onClick={() => removeFeature(offerIndex, i)}
                                                    className="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:bg-red-50 rounded"
                                                >
                                                    <PiTrash />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    {isSelected && (
                                        <button
                                            onClick={() => addFeature(offerIndex)}
                                            className="flex items-center gap-2 text-gray-400 hover:text-blue-500 dashed border border-transparent hover:border-blue-200 rounded px-2"
                                        >
                                            <PiPlus /> Add Feature
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="w-full md:w-[400px] p-12 rounded-[2.5rem] text-white text-center relative overflow-hidden group" style={{ backgroundColor: primaryColor }}>
                                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                <span className="relative z-10 text-xs font-black uppercase tracking-[0.4em] mb-6 block opacity-70">
                                    <EditableText
                                        value={offer.name || "MEMBERSHIP"}
                                        onChange={(v) => updatePlan(offerIndex, 'name', v)}
                                        as="span"
                                    />
                                </span>
                                <div className="relative z-10 flex justify-center items-baseline gap-1 my-8 text-white">
                                    <span className="text-8xl font-black leading-none tracking-tighter">
                                        $<EditableText value={offer.price} onChange={(v) => updatePlan(offerIndex, 'price', v)} className="inline text-white" />
                                    </span>
                                    <span className="text-lg font-bold opacity-70">
                                        <EditableText value={offer.period || "/MO"} onChange={(v) => updatePlan(offerIndex, 'period', v)} className="inline text-white" />
                                    </span>
                                </div>
                                <p className="relative z-10 text-xs font-bold mb-10 uppercase tracking-widest opacity-70">
                                    <EditableText
                                        value={offer.desc || "Billed annually, cancel anytime"}
                                        onChange={(v) => updatePlan(offerIndex, 'desc', v)}
                                        className="text-white"
                                        as="span"
                                    />
                                </p>
                                {offer.buttonLink ? (
                                    <a
                                        href={onContentChange ? undefined : offer.buttonLink}
                                        target={offer.buttonLink.startsWith('http') && !onContentChange ? "_blank" : undefined}
                                        rel={offer.buttonLink.startsWith('http') && !onContentChange ? "noopener noreferrer" : undefined}
                                        className="relative z-10 block w-full py-5 rounded-2xl bg-white text-gray-900 font-black text-sm uppercase tracking-widest hover:scale-105 transition-transform text-center"
                                        onClick={(e) => {
                                            if (onContentChange) e.preventDefault();
                                        }}
                                    >
                                        <EditableText
                                            value={offer.buttonText || "Start Now"}
                                            onChange={(v) => updatePlan(offerIndex, 'buttonText', v)}
                                            as="span"
                                        />
                                    </a>
                                ) : (
                                    <button className="relative z-10 w-full py-5 rounded-2xl bg-white text-gray-900 font-black text-sm uppercase tracking-widest hover:scale-105 transition-transform">
                                        <EditableText
                                            value={offer.buttonText || "Start Now"}
                                            onChange={(v) => updatePlan(offerIndex, 'buttonText', v)}
                                            as="span"
                                        />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    // Default "Model A" - 3 Column Grid
    return (
        <section className={`px-6 py-24 bg-white ${isSelected ? "ring-2 ring-blue-500" : ""}`} style={{ fontFamily }}>
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-20">
                    <span className="text-xs font-black uppercase tracking-[0.3em] text-gray-400 mb-4 block">
                        <EditableText value={content.badgeText || "PLANS & PRICING"} onChange={(v) => onContentChange?.({ ...content, badgeText: v })} as="span" />
                    </span>
                    <h2 className="text-5xl md:text-6xl font-black text-gray-900 tracking-tight mb-6">
                        <EditableText value={content.title} onChange={(v) => onContentChange?.({ ...content, title: v })} placeholder="Section Title" />
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch justify-center">
                    {plans.map((p: any, i: number) => (
                        <div
                            key={i}
                            className={`relative p-10 rounded-3xl border-2 transition-all duration-300 hover:shadow-xl group ${p.popular
                                ? 'border-gray-900 bg-gray-900 text-white transform scale-105 z-10 py-14'
                                : 'border-gray-100 bg-white text-gray-900 hover:border-gray-200'
                                }`}
                        >
                            {isSelected && (
                                <div className="absolute top-2 right-2 flex gap-2 z-50 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); togglePopular(i); }}
                                        className={`p-2 rounded-full ${p.popular ? "bg-yellow-400 text-black" : "bg-gray-100 text-gray-400"}`}
                                        title="Toggle Popular"
                                    >
                                        <PiStarFill />
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); removePlan(i); }}
                                        className="p-2 rounded-full bg-red-100 text-red-500 hover:bg-red-200"
                                        title="Remove Plan"
                                    >
                                        <PiTrash />
                                    </button>
                                </div>
                            )}

                            {p.popular && (
                                <div
                                    className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest text-white shadow-lg"
                                    style={{ backgroundColor: secondaryColor }}
                                >
                                    <EditableText
                                        value={p.badge || "Most Popular"}
                                        onChange={(v) => updatePlan(i, 'badge', v)}
                                        className="text-white"
                                    />
                                </div>
                            )}

                            <h3 className={`text-2xl font-black mb-2 uppercase tracking-tight ${p.popular ? 'text-white' : 'text-gray-900'}`}>
                                <EditableText value={p.name} onChange={(v) => updatePlan(i, 'name', v)} placeholder="Plan Name" />
                            </h3>
                            <div className={`text-sm font-medium mb-10 ${p.popular ? 'text-gray-400' : 'text-gray-500'}`}>
                                <EditableText value={p.desc} onChange={(v) => updatePlan(i, 'desc', v)} placeholder="Description" />
                            </div>

                            <div className="flex items-baseline gap-1 mb-10">
                                <span className="text-6xl font-black tracking-tighter">$<EditableText value={p.price} onChange={(v) => updatePlan(i, 'price', v)} className="inline" /></span>
                                <span className={`text-xs font-bold uppercase ${p.popular ? 'text-gray-500' : 'text-gray-400'}`}>
                                    <EditableText value={p.period || "/mo"} onChange={(v) => updatePlan(i, 'period', v)} className="inline" />
                                </span>
                            </div>

                            <ul className="space-y-4 mb-12">
                                {p.features.map((f: string, j: number) => (
                                    <li key={j} className="flex items-center gap-3 font-bold text-sm relative group/feature">
                                        <PiCheckCircleFill
                                            className={`text-lg flex-shrink-0 ${p.popular ? 'text-blue-400' : 'text-green-500'}`}
                                        />
                                        <EditableText value={f} onChange={(v) => updateFeature(i, j, v)} />
                                        {isSelected && (
                                            <button
                                                onClick={() => removeFeature(i, j)}
                                                className="absolute right-0 opacity-0 group-hover/feature:opacity-100 text-red-500"
                                            >
                                                <PiTrash size={14} />
                                            </button>
                                        )}
                                    </li>
                                ))}
                                {isSelected && (
                                    <li
                                        className="flex items-center gap-2 text-xs opacity-50 hover:opacity-100 cursor-pointer p-1 border border-dashed rounded justify-center"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            addFeature(i);
                                        }}
                                    >
                                        <PiPlus /> Add Feature
                                    </li>
                                )}
                            </ul>

                            {p.buttonLink ? (
                                <a
                                    href={onContentChange ? undefined : p.buttonLink}
                                    target={p.buttonLink.startsWith('http') && !onContentChange ? "_blank" : undefined}
                                    rel={p.buttonLink.startsWith('http') && !onContentChange ? "noopener noreferrer" : undefined}
                                    className={`block text-center w-full py-4 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${p.popular
                                        ? 'bg-white text-gray-900 hover:bg-gray-100'
                                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                                        }`}
                                    onClick={(e) => {
                                        if (onContentChange) e.preventDefault();
                                    }}
                                >
                                    <EditableText value={p.buttonText || "Select Plan"} onChange={(v) => updatePlan(i, 'buttonText', v)} as="span" />
                                </a>
                            ) : (
                                <button
                                    className={`w-full py-4 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${p.popular
                                        ? 'bg-white text-gray-900 hover:bg-gray-100'
                                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                                        }`}
                                >
                                    <EditableText value={p.buttonText || "Select Plan"} onChange={(v) => updatePlan(i, 'buttonText', v)} as="span" />
                                </button>
                            )}
                        </div>
                    ))}

                    {isSelected && (
                        <div className="flex items-center justify-center min-h-[400px]">
                            <button
                                onClick={addPlan}
                                className="flex flex-col items-center justify-center h-40 w-40 rounded-full border-2 border-dashed border-gray-300 hover:border-blue-500 hover:text-blue-500 text-gray-400 transition-all font-bold"
                            >
                                <PiPlus className="text-3xl mb-2" />
                                Add Plan
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
